// src/pages/AIChat.js
import React, { useState, useRef, useEffect } from "react";
import { submitChatPrompt } from "../api";

import ReactMarkdown from "react-markdown";

export default function AIChat() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hello! I am your AI Assistant. How can I help you today? I can help with exam preparation, explain complex formulas, or assist with any other questions you have." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setInput("");
    const newMessages = [...messages, { role: "user", text: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    try {
      // Send message to backend (passing history excluding initial greeting)
      const history = newMessages.slice(1); 
      const res = await submitChatPrompt(userMsg, history);
      
      setMessages(prev => [...prev, { role: "assistant", text: res.text }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: "assistant", text: "⚠️ Sorry, I encountered an error: " + err.message }]);
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    if (window.confirm("Are you sure you want to clear this conversation?")) {
      setMessages([{ role: "assistant", text: "Hello! I am your AI Assistant. How can I help you today?" }]);
    }
  };

  return (
    <div className="fade-in" style={{ height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
      {/* Custom styles for Markdown content */}
      <style>{`
        .markdown-content p { margin-bottom: 10px; }
        .markdown-content p:last-child { margin-bottom: 0; }
        .markdown-content ul, .markdown-content ol { margin-left: 20px; margin-bottom: 10px; }
        .markdown-content li { margin-bottom: 4px; }
        .markdown-content strong { font-weight: 700; color: inherit; }
        .markdown-content code { background: rgba(0,0,0,0.05); padding: 2px 4px; border-radius: 4px; font-family: monospace; }
      `}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 className="page-title">AI Assistant</h1>
          <p className="page-sub">Powered by Gemini 2.5 Flash &middot; Instant help with any topic</p>
        </div>
        <button className="btn btn-sm" onClick={clearChat} style={{ color: "var(--red)" }}>
          🗑️ Clear
        </button>
      </div>

      {/* Chat Container */}
      <div className="card" style={{ flex: 1, display: "flex", flexDirection: "column", padding: 0, overflow: "hidden", border: "1px solid var(--border)" }}>
        {/* Messages area */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "24px", display: "flex", flexDirection: "column", gap: 16, background: "#fbfbfb" }}>
          {messages.map((m, i) => (
            <div key={i} style={{ 
              display: "flex", 
              justifyContent: m.role === "user" ? "flex-end" : "flex-start" 
            }}>
              <div style={{ 
                maxWidth: "85%", 
                padding: "12px 18px",
                borderRadius: "var(--radius-lg)",
                fontSize: "14px",
                lineHeight: "1.6",
                boxShadow: "var(--shadow-sm)",
                background: m.role === "user" ? "var(--blue)" : "var(--surface)",
                color: m.role === "user" ? "#fff" : "var(--text-primary)",
                border: m.role === "user" ? "none" : "1px solid var(--border)",
                borderTopRightRadius: m.role === "user" ? "2px" : "var(--radius-lg)",
                borderTopLeftRadius: m.role === "assistant" ? "2px" : "var(--radius-lg)",
              }}>
                {m.role === "user" ? (
                  m.text
                ) : (
                  <div className="markdown-content">
                    <ReactMarkdown>{m.text}</ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", color: "var(--text-muted)", fontSize: 13 }}>
              <div className="spinner" style={{ width: 14, height: 14, borderWidth: "1.5px" }}></div>
              AI Tutor is thinking...
            </div>
          )}
        </div>

        {/* Input area */}
        <div style={{ padding: "20px", borderTop: "1px solid var(--border)", background: "var(--surface)" }}>
          <form onSubmit={handleSend} style={{ display: "flex", gap: 10 }}>
            <input 
              type="text" 
              className="form-input" 
              placeholder="Ask me anything about your exams..." 
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading}
              style={{ borderRadius: "var(--radius-md)", fontSize: "14px" }}
            />
            <button className="btn btn-blue" disabled={loading || !input.trim()} style={{ width: 80, height: 42, borderRadius: "var(--radius-md)" }}>
              {loading ? "..." : "Send"}
            </button>
          </form>
          <div style={{ marginTop: 8, fontSize: 11, color: "var(--text-muted)", textAlign: "center" }}>
            AI can make mistakes. Verify important formulas with your study material.
          </div>
        </div>
      </div>
    </div>
  );
}
